import React, {ReactNode, useState, useRef, useEffect, useContext, ReactElement} from 'react';

import DeviceContext, {Device} from '../../../RootProvider/DeviceContext';

import {ArrowIcon} from '../Icons';
import {IconRotation, IconSize} from '../Icons/IconWrapper';
import './Dropdown.scss';

interface Props {
  buttonContent: ReactNode;
  buttonClass?: string;
  hideCaret?: boolean;
  hideCaretMobile?: boolean;
  className?: string;
  children?: ReactNode;
}

function Dropdown({buttonContent, buttonClass, hideCaret, hideCaretMobile, className, children}: Props): ReactElement {
  const device = useContext(DeviceContext);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  // close if clicked outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    // only setup handler if dropdown is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // teardown
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef, isOpen]);

  return (
    <div ref={wrapperRef} className={`dropdown ${className || ''}`.trim()}>
      <button
        type="button"
        className={`dropdown__button button--unstyled ${buttonClass || ''}`.trim()}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="ui.dropdown.toggle"
      >
        {buttonContent}
        {!hideCaret && !(hideCaretMobile && device === Device.MOBILE) && (
          <ArrowIcon rotation={IconRotation.CW_90} size={IconSize.SMALL} className="dropdown__icon" />
        )}
      </button>
      <div
        className={`dropdown__panel dropdown__panel--${isOpen ? 'open' : 'closed'}`.trim()}
        data-testid="ui.dropdown.panel"
      >
        {children}
      </div>
    </div>
  );
}

export default Dropdown;
